import { memo, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useModal from '../../../../../../hooks/modalHook';
import QuoteWidgetCalculator from './Calculator';
import Tooltip from '../../../../../../components/ToolTip';
import { CLEAR_QUOTE_WIDGET_PAYLOAD } from '../../../../../../states/slices/quoteWidgetSlice';
import { useDispatch } from 'react-redux';
import useUserAccount from '../../../../../../hooks/userAccountHook';
import useLayoutHook from '../../../../../../hooks/layoutHook';

interface IFloatingCalculatorButtonProps {
  isFloat?: boolean;
}
export const FloatingCalculatorButton = memo(({ isFloat }: IFloatingCalculatorButtonProps) => {
  const { updateModalStates, ModalComponent, closeModal } = useModal();
  const {user} = useUserAccount();
  const { screenWidth } = useLayoutHook();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const DRAG_THRESHOLD = 5;
  const dispatch = useDispatch();
  const handleQuoteCalculator = () => {
    updateModalStates({
      show: true,
      size: '4xl',
      children: <QuoteWidgetCalculator onclose={closeModal}/>,
      static: true,
      title: screenWidth <= 550 ? 'Motor Insurance Calculator' : 'Motor Insurance Premium Calculator',
    });
  };

  useEffect(() => {
    const handleResize = () => {
      const { innerWidth, innerHeight } = window;
      positionRef.current = {
        x: Math.min(Math.max(positionRef.current.x, -innerWidth + 100), innerWidth - 100),
        y: Math.min(Math.max(positionRef.current.y, -innerHeight + 100), innerHeight - 100)
      };
      setPosition(positionRef.current);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleButtonClick = () => {
    if (!isDragging.current) {
      handleQuoteCalculator();
    }
    isDragging.current = false;
      // if(!window.location.pathname.includes('dynamic-create-policy')){
      //   dispatch(CLEAR_QUOTE_WIDGET_PAYLOAD())
      // }
     

  };

  return (
    <>
      {isFloat ? (
        <AnimatePresence>
          <motion.div
            drag
            dragMomentum={false}
            dragElastic={0.2}
            dragConstraints={{
              top: -window.innerHeight + 100,
              left: -window.innerWidth + 100,
              right: window.innerWidth - 100,
              bottom: window.innerHeight - 100,
            }}
            onDragStart={() => {
              isDragging.current = false;
              dragStartPos.current = { x: position.x, y: position.y };
            }}
            onDrag={(e, info) => {
              if (Math.abs(info.offset.x) > DRAG_THRESHOLD ||
                Math.abs(info.offset.y) > DRAG_THRESHOLD) {
                isDragging.current = true;
              }
            }}
            onDragEnd={(e, info) => {
              const newPosition = {
                x: positionRef.current.x + info.offset.x,
                y: positionRef.current.y + info.offset.y
              };
              positionRef.current = newPosition;
              setPosition(newPosition);
            }}
            initial={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              x: position.x,
              y: position.y,
              transition: { type: 'spring', damping: 30, stiffness: 300 }
            }}
            exit={{ opacity: 0, scale: 0.5, y: 50 }}
            whileHover={{ scale: 1.05, cursor: 'grab' }}
            whileTap={{
              scale: 0.98,
              cursor: 'grabbing',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            className="fixed bottom-8 right-8 z-50 touch-none"
          >
            <motion.button
              onClick={handleButtonClick}
              className="group relative flex items-center gap-3 pl-3 pr-5 h-14 rounded-full bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 text-gray-900 shadow-[0_18px_35px_-18px_rgba(245,158,11,0.9)] hover:shadow-[0_22px_45px_-20px_rgba(245,158,11,1)] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2"
              aria-label="Get Quote"
              whileTap={{ scale: isDragging.current ? 1 : 0.95 }}
            >
              <span className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10" />
              <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-sm ring-1 ring-black/10">
                <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              </span>
              <span className="relative flex flex-col items-start leading-none">
                <span className="text-[13px] font-semibold tracking-wide whitespace-nowrap">Create Policy</span>
                <span className="text-[10px] font-medium text-gray-800/80 whitespace-nowrap">Instant premium estimate</span>
              </span>
            </motion.button>
          </motion.div>
        </AnimatePresence>
      ) : (
        <Tooltip placement="bottom" bgColor="bg-gray-200" message={user.isInternalAdmin() ? 'Get a quote' : 'Create a policy'}>
          <button
            type="button"
            className="group inline-flex items-center gap-2 h-9 px-3.5 rounded-full bg-gradient-to-r from-yellow-400 to-amber-400 text-gray-900 shadow-sm hover:shadow-md hover:from-yellow-400 hover:to-yellow-500 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2"
            onClick={handleButtonClick}
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/70 ring-1 ring-black/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </span>
            <span className="text-xs font-semibold">{user.isInternalAdmin() ? 'Get Quote' : 'Create Policy'}</span>
          </button>
        </Tooltip>
      )}

      <ModalComponent />
    </>
  );
});

FloatingCalculatorButton.displayName = 'FloatingCalculatorButton';
